
import {
  defineComponent, onMounted,
  PropType, Ref,
  ref,
  watch,
  onBeforeUnmount,
  computed,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';

import {
  TabItem,
  TabSubitem,
  ParserObject,
  TemplateStructureEnum,
} from '@/types';

import Tooltip from 'primevue/tooltip';

import ConfigurationPanelTabContentItem from '@/components/template-builder/panels/ConfigurationPanelTabContentItem.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import HelpMessage from '@/components/common/HelpMessage.vue';

export default defineComponent({
  name: 'ConfigurationPanelTabContent',

  components: {
    ConfigurationPanelTabContentItem,
    SpmButton,
    HelpMessage,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    tabItem: {
      type: Object as PropType<TabItem | TabSubitem>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserObject>,
      required: true,
    },

    displayLabel: {
      type: Boolean,
      required: false,
      default: true,
    },

    headerTitle: {
      type: String,
      required: false,
      default: '',
    },

    isSynchronizedPage: {
      type: Boolean,
      required: false,
      default: false,
    },

    structureActions: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  emits: ['refresh'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const activeFieldGroupIndex: Ref<number[]> = ref([]);
    const toggleFieldGroup = (index: number) => {
      if (activeFieldGroupIndex.value.includes(index)) {
        activeFieldGroupIndex.value.splice(activeFieldGroupIndex.value.indexOf(index), 1);
      } else {
        activeFieldGroupIndex.value.push(index);
      }
    };
    const selectedSyncElement = computed(() => store.getters['liveEditor/getSelectedSyncElement']);
    const selectedStructure = computed(() => store.getters['liveEditor/getSelectedStructure']);
    const itemContainsElementPartType = (item: any, partType: string): boolean => {
      if (!item || !partType) return false;
      if (item.elementPartType === partType) return true;
      if (Array.isArray(item.tabPanel?.items)) {
        return item.tabPanel.items.some((tabPanelItem: any) => itemContainsElementPartType(tabPanelItem, partType));
      }
      if (Array.isArray(item.fieldGroups)) {
        return item.fieldGroups.some((fieldGroupItem: any) => itemContainsElementPartType(fieldGroupItem, partType));
      }
      return false;
    };

    const tabItems = computed(() => {
      if (props.tabItem.tabPanel && props.tabItem.tabPanel.items) {
        if (selectedSyncElement.value && selectedSyncElement.value.elementPartType) {
          const filteredItems = props.tabItem.tabPanel.items.filter(
            (item: any) => itemContainsElementPartType(item, selectedSyncElement.value.elementPartType),
          );
          return filteredItems.length > 0 ? filteredItems : props.tabItem.tabPanel.items;
        }
        return props.tabItem.tabPanel.items;
      }
      return [];
    });

    const targetAnchor = computed(() => store.getters['liveEditor/getTargetAnchor']);

    const handleRefresh = () => {
      emit('refresh');
    };

    const stopWatchers: any[] = [];

    const initActiveFieldGroupIndex = (items: any[]) => {
      activeFieldGroupIndex.value = items.reduce((acc: number[], item, index) => {
        // Open first accordion by default, or any accordion with defaultOpen: true
        if (index === 0 || item.defaultOpen) {
          acc.push(index);
        }
        return acc;
      }, []);
    };

    stopWatchers.push(watch([
      () => props.parserValues,
      () => props.tabItem,
    ],
    () => {
      // Don't reset accordions if we have an active anchor navigation
      if (props.tabItem.tabPanel && !targetAnchor.value) {
        // [DESIGN-PANEL-PERF] Only open first accordion by default to avoid rendering all components at once
        // Previously opened ALL accordions which caused 3.5s freeze due to ~56 components rendering simultaneously
        initActiveFieldGroupIndex(props.tabItem.tabPanel.items);
      }
    }));

    stopWatchers.push(watch(selectedSyncElement, (newValue, oldValue) => {
      if (props.tabItem.tabPanel && !oldValue) {
        initActiveFieldGroupIndex(props.tabItem.tabPanel.items);
      }
    }, { deep: true }));

    const enterEditSyncElement = () => {
      const elementType = TemplateStructureEnum.DESIGN;
      const name = props.headerTitle;

      store.dispatch('liveEditor/showSyncElementWarning', {
        elementId: selectedStructure.value.identifier,
        elementType,
        name,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        acceptCallback: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        rejectCallback: () => {},
      });
    };

    // Watch for anchor changes to scroll to specific accordion
    stopWatchers.push(watch(targetAnchor, (anchor) => {
      if (anchor && tabItems.value.length > 0) {
        // Use nextTick to ensure other watchers have finished
        nextTick(() => {
          const targetIndex = tabItems.value.findIndex(
            (item: any) => item.anchorId === anchor,
          );

          if (targetIndex !== -1) {
            // Close all accordions and open only the targeted one
            activeFieldGroupIndex.value = [targetIndex];

            // Wait for DOM update, then scroll
            nextTick(() => {
              const element = document.querySelector(`[data-anchor-id="${anchor}"]`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
              // Reset anchor after use
              store.commit('liveEditor/setTargetAnchor', null);
            });
          }
        });
      }
    }, { immediate: true }));

    onMounted(() => {
      const savedIndices = store.getters['liveEditor/getSavedFieldGroupIndices'];
      if (savedIndices !== null) {
        activeFieldGroupIndex.value = savedIndices;
        store.commit('liveEditor/setSavedFieldGroupIndices', null);
      } else if (props.tabItem.tabPanel) {
        initActiveFieldGroupIndex(props.tabItem.tabPanel.items);
      }
    });

    onBeforeUnmount(() => {
      store.commit('liveEditor/setSavedFieldGroupIndices', [...activeFieldGroupIndex.value]);
      stopWatchers.forEach((stop) => stop());
    });

    return {
      activeFieldGroupIndex,
      tabItems,
      toggleFieldGroup,
      handleRefresh,
      t,
      enterEditSyncElement,
      selectedSyncElement,
    };
  },
});
